import type { SignupInput } from '@/api/client'
import { ApiError } from '@/api/client'
import type { Profile, SlotId } from '@/profile/types'
import type { OnboardingEffect } from './engine'

/**
 * Getting each answer out of memory as soon as it is given.
 *
 * The obvious shape is one submission at the end, and it is wrong in exactly
 * the case that matters most. Somebody who gives their name and then walks
 * away, or loses signal, or is called out of the room, is the person for whom
 * being remembered is worth the most, and a single write at the end stores
 * nothing for them at all. So every completed step writes, twice: to the
 * device, which is synchronous and always works, and to the server, which is
 * neither.
 *
 * Three rules run through this file, and each one is a specific failure rather
 * than a principle.
 *
 * A server write never fails a conversation. The local copy has already
 * landed, the person is mid-sentence, and there is nothing useful to say to
 * them about an HTTP status. Failures are held and folded into the next write.
 *
 * Nothing personal goes to the server before consent has. If the consent call
 * fails and the name call succeeds, the server holds a name with no recorded
 * permission to hold it, which is the one outcome the whole consent step
 * exists to prevent. So a failed consent write blocks the profile writes
 * behind it rather than being retried alongside them.
 *
 * Field names are not guessed. `namu-server`'s validator builds its result
 * from the fields it declares and ignores everything else, so a payload with
 * `region` where the route wants `city` is accepted, returns 200, and stores
 * nothing. Every name here is the one in
 * `namu-server/src/http/routes/profile.ts`.
 */

/** The slice of the API client this needs, so a test can supply two functions. */
export interface ProfileApi {
  signUp(input: SignupInput): Promise<unknown>
  setConsent(granted: boolean): Promise<void>
}

/** The device's own copy. `useProfileStore.getState().commit` satisfies this. */
export interface ProfileStore {
  commit(profile: Profile): void
}

export interface ProfileWriterOptions {
  local: ProfileStore
  /** Null in a build with no API URL, which is how the app runs offline. */
  api?: ProfileApi | null
  /** Reports a write that could not be made. Never shown to the person. */
  onError?(error: unknown, slot: SlotId | 'complete'): void
}

export interface WriteOutcome {
  /** Always true: the local write cannot fail. */
  local: boolean
  /** False when the server was unreachable or refused, or is not configured. */
  remote: boolean
  /** Fields still waiting to reach the server. */
  deferred: readonly string[]
}

/**
 * Turns a completed step into the payload the server validates.
 *
 * `language` is deliberately absent. There is no language column on the
 * profile route, so sending one would be silently dropped, and a write that
 * looks like it happened is worse than one that plainly did not. The locale
 * lives on the device, where the thing that needs it runs.
 */
function fieldsFor(slot: SlotId, profile: Profile): SignupInput | null {
  switch (slot) {
    case 'name': {
      const name = profile.name?.value
      return name === undefined ? null : { givenName: name, complete: false }
    }
    case 'region': {
      const region = profile.region?.value
      return region === undefined ? null : { city: region, complete: false }
    }
    case 'ageBand': {
      const band = profile.ageBand?.value
      /*
       * `ageBand`, not `age` and not `birthYear`.
       *
       * The app models a band and the column used to be `birth_year` only.
       * Those do not convert honestly in either direction: turning "35 to 49"
       * into a year of birth means inventing a midpoint and writing a fact
       * nobody stated into a database, and turning a year back into a band is
       * only right until the person's birthday. Migration 0013 adds the column
       * so neither conversion has to happen.
       */
      return band === undefined ? null : { ageBand: band, complete: false }
    }
    case 'gender': {
      const gender = profile.gender?.value
      return gender === undefined ? null : { gender, complete: false }
    }
    case 'language':
    case 'consent':
    case 'interests':
      return null
  }
}

export interface ProfileWriter {
  /**
   * Handles the write-bearing effects and ignores the rest, so a host can pipe
   * everything the engine produces through one call.
   */
  apply(effect: OnboardingEffect): Promise<WriteOutcome | null>
  /** Retries whatever is still waiting. Worth calling when connectivity returns. */
  flush(): Promise<WriteOutcome>
}

export function createProfileWriter(options: ProfileWriterOptions): ProfileWriter {
  const { local, api = null, onError } = options

  /**
   * Fields that have not reached the server yet, merged rather than queued.
   *
   * A queue would replay "city: Kano" followed by "city: Sokoto" in order,
   * which is two requests to reach a state one request describes. The server
   * route is an upsert of whatever it is sent, so the last value of each field
   * is the only one worth sending.
   */
  let deferredFields: SignupInput = {}
  /** Null when there is nothing owed. */
  let deferredConsent: boolean | null = null

  function report(error: unknown, slot: SlotId | 'complete'): void {
    /*
     * An offline device is the normal case here, not an incident. Reporting it
     * as an error trained everybody to ignore the channel, which meant the
     * genuine 400 from a renamed field went unnoticed for a week.
     */
    if (error instanceof ApiError && error.status === 0) return
    onError?.(error, slot)
  }

  /** Sends consent first, and reports whether the profile writes may follow. */
  async function settleConsent(): Promise<boolean> {
    if (deferredConsent === null) return true
    if (!api) return false
    const granted = deferredConsent
    try {
      await api.setConsent(granted)
      deferredConsent = null
      return true
    } catch (error) {
      report(error, 'consent')
      return false
    }
  }

  async function send(slot: SlotId | 'complete'): Promise<WriteOutcome> {
    const consentSettled = await settleConsent()
    const pending = deferredFields
    const nothingOwed = Object.keys(pending).length === 0

    if (!api || !consentSettled || nothingOwed) {
      return {
        local: true,
        remote: consentSettled && nothingOwed,
        deferred: Object.keys(deferredFields),
      }
    }

    try {
      await api.signUp(pending)
      deferredFields = {}
      return { local: true, remote: true, deferred: [] }
    } catch (error) {
      report(error, slot)
      return { local: true, remote: false, deferred: Object.keys(deferredFields) }
    }
  }

  return {
    async apply(effect) {
      if (effect.kind === 'persist') {
        // Local first and unconditionally. The store applies the consent gate
        // itself, so nothing is written to disk that consent does not cover.
        local.commit(effect.profile)

        if (effect.slot === 'consent') {
          const granted = effect.profile.consent?.value
          if (granted === undefined) return null
          deferredConsent = granted
          return send('consent')
        }

        /*
         * The remote gate, stated separately from the local one.
         *
         * `profileStore` refuses to write anything but `completedAt` without
         * consent, so relying on it would be enough for the device. The server
         * has no such store in front of it, and a name posted to it without
         * consent is a promise broken in the one place the person cannot see.
         */
        if (effect.profile.consent?.value !== true) {
          return { local: true, remote: false, deferred: Object.keys(deferredFields) }
        }

        const fields = fieldsFor(effect.slot, effect.profile)
        if (!fields) return null
        deferredFields = { ...deferredFields, ...fields }
        return send(effect.slot)
      }

      if (effect.kind === 'settle') {
        local.commit(effect.profile)
        if (effect.profile.consent?.value !== true) return null
        /*
         * `complete: true` only once the conversation is genuinely over. Every
         * write before this sends false, which is what stops a half-finished
         * profile from being read back as a finished one and never asked about
         * again.
         */
        deferredFields = { ...deferredFields, complete: true }
        return send('complete')
      }

      return null
    },

    flush() {
      return send('complete')
    },
  }
}
